<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="900px"
    :before-close="handleClose"
    @open="handleOpen"
    custom-class="member-selector-dialog"
    top="5vh">
    
    <div slot="title" class="dialog-title">
      <i class="el-icon-user-solid"></i>
      <span>管理工作组成员</span>
      <el-tag v-if="isOwner === 0" type="info" size="small" class="view-only-tag">
        <i class="el-icon-view"></i> 仅查看
      </el-tag>
    </div>
    
    <div class="member-selector-container">
      <!-- 左侧：可添加成员列表 -->
      <div class="transfer-panel left-panel">
        <div class="panel-header">
          <div class="header-left">
            <i class="el-icon-user"></i>
            <span class="panel-title">可添加成员</span>
          </div>
          <div class="header-right">
            <el-tooltip content="已选择/总数（支持跨页选择）" placement="top">
              <span class="count-badge">{{ selectedUserIds.length }}/{{ totalUsers }}</span>
            </el-tooltip>
            <el-tooltip content="全选/取消全选当前页" placement="top">
              <el-checkbox
                v-if="isOwner === 1 && availableUsers.length > 0"
                v-model="selectAllLeft"
                @change="handleSelectAllLeft"
                :indeterminate="isIndeterminateLeft"
                class="select-all-checkbox">
              </el-checkbox>
            </el-tooltip>
          </div>
        </div>
        
        <div class="panel-body">
           <div class="search-wrapper">
             <el-input
               v-model="searchKeyword"
               placeholder="搜索成员姓名或账号"
               prefix-icon="el-icon-search"
               size="medium"
               clearable
               @input="handleSearch">
             </el-input>
           </div>
          
          <div class="list-content" v-loading="loadingUsers">
            <el-checkbox-group v-model="selectedUserIds" v-if="isOwner === 1">
              <div 
                v-for="user in availableUsers" 
                :key="user.id"
                class="user-card"
                :class="{ 'selected': selectedUserIds.includes(user.id) }">
                <el-checkbox 
                  :label="user.id"
                  class="user-checkbox">
                  <div class="user-info">
                    <div class="user-avatar">
                      <i class="el-icon-user-solid"></i>
                    </div>
                    <div class="user-details">
                      <div class="user-name">{{ user.userName }}</div>
                       <div class="user-dept" v-if="user.userOperatorId">
                         <i class="el-icon-postcard"></i>
                         {{ user.userOperatorId }}
                       </div>
                    </div>
                  </div>
                </el-checkbox>
              </div>
            </el-checkbox-group>
            
            <!-- 非管理员只读视图 -->
            <div v-if="isOwner === 0">
              <div 
                v-for="user in availableUsers" 
                :key="user.id"
                class="user-card readonly">
                <div class="user-info">
                  <div class="user-avatar">
                    <i class="el-icon-user-solid"></i>
                  </div>
                  <div class="user-details">
                    <div class="user-name">{{ user.userName }}</div>
                       <div class="user-dept" v-if="user.userOperatorId">
                         <i class="el-icon-postcard"></i>
                         {{ user.userOperatorId }}
                       </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="availableUsers.length === 0" class="empty-state">
              <i class="el-icon-folder-opened"></i>
              <p>{{ searchKeyword ? '无匹配数据' : '暂无可添加成员' }}</p>
            </div>
          </div>
          
          <div class="pagination-wrapper" v-if="totalUsers > pageSize">
            <el-pagination
              small
              layout="prev, pager, next"
              :total="totalUsers"
              :page-size="pageSize"
              :current-page.sync="currentPage"
              @current-change="loadUserList"
              background>
            </el-pagination>
          </div>
        </div>
      </div>
      
      <!-- 中间：操作按钮 -->
      <div class="transfer-buttons">
        <template v-if="isOwner === 1">
          <el-tooltip content="移除选中成员" placement="top">
            <el-button 
              type="primary"
              circle
              icon="el-icon-back"
              :disabled="selectedMemberIds.length === 0"
              @click="handleBatchRemoveMembers"
              class="action-button remove-btn">
            </el-button>
          </el-tooltip>
          <el-tooltip content="添加选中成员" placement="bottom">
            <el-button 
              type="success"
              circle
              icon="el-icon-right"
              :disabled="selectedUserIds.length === 0"
              @click="handleAddMembers"
              class="action-button add-btn">
            </el-button>
          </el-tooltip>
        </template>
        <div v-else class="view-only-indicator">
          <i class="el-icon-lock"></i>
        </div>
      </div>
      
      <!-- 右侧：已加入成员列表 -->
      <div class="transfer-panel right-panel">
        <div class="panel-header">
          <div class="header-left">
            <i class="el-icon-s-custom"></i>
            <span class="panel-title">已加入成员</span>
          </div>
          <div class="header-right">
            <el-tooltip content="已选择/总数（支持跨页选择）" placement="top">
              <span class="count-badge active">{{ selectedMemberIds.length }}/{{ totalJoinedMembers }}</span>
            </el-tooltip>
            <el-tooltip content="全选/取消全选当前页" placement="top">
              <el-checkbox
                v-if="isOwner === 1 && filteredJoinedMembers.length > 0"
                v-model="selectAllRight"
                @change="handleSelectAllRight"
                :indeterminate="isIndeterminateRight"
                class="select-all-checkbox">
              </el-checkbox>
            </el-tooltip>
          </div>
        </div>
        
        <div class="panel-body">
           <div class="search-wrapper">
             <el-input
               v-model="memberSearchKeyword"
               placeholder="搜索成员姓名或账号"
               prefix-icon="el-icon-search"
               size="medium"
               clearable
               @input="handleMemberSearch">
             </el-input>
           </div>
          
          <div class="list-content" v-loading="loadingMembers">
            <el-checkbox-group v-model="selectedMemberIds" v-if="isOwner === 1">
              <div 
                v-for="member in filteredJoinedMembers" 
                :key="member.id"
                class="user-card"
                :class="{ 'selected': selectedMemberIds.includes(member.id) }">
                <el-checkbox 
                  :label="member.id"
                  class="user-checkbox">
                  <div class="user-info">
                    <div class="user-avatar active">
                      <i class="el-icon-user-solid"></i>
                    </div>
                    <div class="user-details">
                      <div class="user-name">{{ member.userName }}</div>
                       <div class="user-dept" v-if="member.userOperatorId">
                         <i class="el-icon-postcard"></i>
                         {{ member.userOperatorId }}
                       </div>
                    </div>
                  </div>
                </el-checkbox>
              </div>
            </el-checkbox-group>
            
            <!-- 非管理员视图：只读 -->
            <div v-if="isOwner === 0">
              <div 
                v-for="member in filteredJoinedMembers" 
                :key="member.id"
                class="user-card readonly">
                <div class="user-info">
                  <div class="user-avatar active">
                    <i class="el-icon-user-solid"></i>
                  </div>
                  <div class="user-details">
                    <div class="user-name">{{ member.userName }}</div>
                       <div class="user-dept" v-if="member.userOperatorId">
                         <i class="el-icon-postcard"></i>
                         {{ member.userOperatorId }}
                       </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="filteredJoinedMembers.length === 0" class="empty-state">
              <i class="el-icon-user"></i>
              <p>{{ memberSearchKeyword ? '无匹配数据' : '暂无成员' }}</p>
            </div>
          </div>
          
          <div class="pagination-wrapper" v-if="totalJoinedMembers > memberPageSize">
            <el-pagination
              small
              layout="prev, pager, next"
              :total="totalJoinedMembers"
              :page-size="memberPageSize"
              :current-page.sync="currentMemberPage"
              background>
            </el-pagination>
          </div>
        </div>
      </div>
    </div>
    
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose" size="medium">
        <i class="el-icon-close"></i> 关闭
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import {
  getJoinGroupUserList,
  joinGroupMembers,
  delGroupMembers
} from '@/api'

export default {
  name: 'MemberSelector',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    groupId: {
      type: [Number, null],
      default: null
    },
    isGroupOwner: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      loadingUsers: false,
      loadingMembers: false,
      
      // 可选用户列表（未加入的）
      availableUsers: [],
      // 已加入的成员
      joinedMembers: [],
      // 左侧搜索和分页
      searchKeyword: '',
      searchTimer: null,
      currentPage: 1,
      pageSize: 8,
      totalUsers: 0,
      
      // 右侧搜索和分页
      memberSearchKeyword: '',
      currentMemberPage: 1,
      memberPageSize: 8,
      
      // 已选择的用户ID（左侧）
      selectedUserIds: [],
      
      // 已选择要删除的成员ID（右侧）
      selectedMemberIds: [],
      
      // 权限控制
      isOwner: 0  // 是否是工作组创建者
    }
  },
  computed: {
    /**
     * 所有过滤后的已加入成员（未分页）
     */
    allFilteredMembers() {
      if (!this.memberSearchKeyword) {
        return this.joinedMembers
      }
      const keyword = this.memberSearchKeyword.toLowerCase()
      return this.joinedMembers.filter(member => {
        return (
          member.userName.toLowerCase().includes(keyword) ||
          (member.email && member.email.toLowerCase().includes(keyword)) ||
          (member.userOperatorId && member.userOperatorId.toLowerCase().includes(keyword))
        )
      })
    },
    /**
     * 当前页的已加入成员列表（分页后）
     */
    filteredJoinedMembers() {
      const start = (this.currentMemberPage - 1) * this.memberPageSize
      const end = start + this.memberPageSize
      return this.allFilteredMembers.slice(start, end)
    },
    /**
     * 已加入成员总数
     */
    totalJoinedMembers() {
      return this.allFilteredMembers.length
    },
    /**
     * 左侧全选状态（当前页）
     */
    selectAllLeft: {
      get() {
        if (this.availableUsers.length === 0) return false
        const currentPageIds = this.availableUsers.map(u => u.id)
        return currentPageIds.every(id => this.selectedUserIds.includes(id))
      },
      set(val) {
        const currentPageIds = this.availableUsers.map(u => u.id)
        if (val) {
          // 全选：添加当前页所有ID到已选列表
          const newIds = [...new Set([...this.selectedUserIds, ...currentPageIds])]
          this.selectedUserIds = newIds
        } else {
          // 取消全选：从已选列表中移除当前页的ID
          this.selectedUserIds = this.selectedUserIds.filter(id => !currentPageIds.includes(id))
        }
      }
    },
    /**
     * 左侧半选状态（当前页）
     */
    isIndeterminateLeft() {
      if (this.availableUsers.length === 0) return false
      const currentPageIds = this.availableUsers.map(u => u.id)
      const selectedCount = currentPageIds.filter(id => this.selectedUserIds.includes(id)).length
      return selectedCount > 0 && selectedCount < currentPageIds.length
    },
    /**
     * 右侧全选状态（当前页）
     */
    selectAllRight: {
      get() {
        if (this.filteredJoinedMembers.length === 0) return false
        const currentPageIds = this.filteredJoinedMembers.map(m => m.id)
        return currentPageIds.every(id => this.selectedMemberIds.includes(id))
      },
      set(val) {
        const currentPageIds = this.filteredJoinedMembers.map(m => m.id)
        if (val) {
          // 全选：添加当前页所有ID到已选列表
          const newIds = [...new Set([...this.selectedMemberIds, ...currentPageIds])]
          this.selectedMemberIds = newIds
        } else {
          // 取消全选：从已选列表中移除当前页的ID
          this.selectedMemberIds = this.selectedMemberIds.filter(id => !currentPageIds.includes(id))
        }
      }
    },
    /**
     * 右侧半选状态（当前页）
     */
    isIndeterminateRight() {
      if (this.filteredJoinedMembers.length === 0) return false
      const currentPageIds = this.filteredJoinedMembers.map(m => m.id)
      const selectedCount = currentPageIds.filter(id => this.selectedMemberIds.includes(id)).length
      return selectedCount > 0 && selectedCount < currentPageIds.length
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    /**
     * 对话框打开时的处理
     */
    async handleOpen() {
      this.selectedUserIds = []
      this.searchKeyword = ''
      this.currentPage = 1
      await this.loadUserList()
    },
    
    /**
     * 加载用户列表（从一个接口获取，前端分离）
     */
    async loadUserList() {
      // 如果 groupId 为空，不执行加载
      if (!this.groupId) {
        return
      }
      
      this.loadingUsers = true
      this.loadingMembers = true
      try {
        const response = await getJoinGroupUserList({
          group_id: this.groupId
        })
        
        const data = response.data
        const allUsers = data.userList || []
        this.isOwner = this.isGroupOwner ? 1 : 0
        
        // 分离已加入和未加入的用户
        const notJoinedUsers = allUsers.filter(user => user.isJoin === 0)
        this.joinedMembers = allUsers.filter(user => user.isJoin === 1)
        
        // 前端搜索过滤
              let filteredUsers = notJoinedUsers
              if (this.searchKeyword) {
                const keyword = this.searchKeyword.toLowerCase()
                filteredUsers = notJoinedUsers.filter(user => {
                  return (
                    user.userName.toLowerCase().includes(keyword) ||
                    (user.email && user.email.toLowerCase().includes(keyword)) ||
                    (user.userOperatorId && user.userOperatorId.toLowerCase().includes(keyword))
                  )
                })
              }
        
        // 前端分页
        this.totalUsers = filteredUsers.length
        const start = (this.currentPage - 1) * this.pageSize
        const end = start + this.pageSize
        this.availableUsers = filteredUsers.slice(start, end)
        
      } catch (error) {
        console.error('加载用户列表失败:', error)
        this.$message.error('加载用户列表失败')
      } finally {
        this.loadingUsers = false
        this.loadingMembers = false
      }
    },
    
    /**
     * 搜索处理（防抖） - 左侧
     */
    handleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.loadUserList()  // 重新加载并过滤
      }, 500)
    },
    
    /**
     * 搜索成员（右侧） - 前端实时过滤
     */
    handleMemberSearch() {
      // 重置到第一页
      this.currentMemberPage = 1
      // 不清空选择，保持已选状态
    },
    
    /**
     * 左侧全选/取消全选（当前页）
     */
    handleSelectAllLeft(val) {
      const currentPageIds = this.availableUsers.map(u => u.id)
      if (val) {
        // 全选当前页
        const newIds = [...new Set([...this.selectedUserIds, ...currentPageIds])]
        this.selectedUserIds = newIds
      } else {
        // 取消全选当前页
        this.selectedUserIds = this.selectedUserIds.filter(id => !currentPageIds.includes(id))
      }
    },
    
    /**
     * 右侧全选/取消全选（当前页）
     */
    handleSelectAllRight(val) {
      const currentPageIds = this.filteredJoinedMembers.map(m => m.id)
      if (val) {
        // 全选当前页
        const newIds = [...new Set([...this.selectedMemberIds, ...currentPageIds])]
        this.selectedMemberIds = newIds
      } else {
        // 取消全选当前页
        this.selectedMemberIds = this.selectedMemberIds.filter(id => !currentPageIds.includes(id))
      }
    },
    
    /**
     * 添加成员
     */
    async handleAddMembers() {
      if (this.selectedUserIds.length === 0) {
        this.$message.warning('请选择要添加的成员')
        return
      }
      
      try {
        const response = await joinGroupMembers({
          group_id: this.groupId,
          user_ids: this.selectedUserIds
        })
        
        this.$message.success(response.message || '添加成功')
        this.selectedUserIds = []
        
        // 重新加载列表
        await this.loadUserList()
        
        // 通知父组件刷新
        this.$emit('members-updated')
      } catch (error) {
        console.error('添加成员失败:', error)
        this.$message.error(error.message || '添加成员失败')
      }
    },
    
    /**
     * 批量删除成员
     */
    handleBatchRemoveMembers() {
      if (this.selectedMemberIds.length === 0) {
        this.$message.warning('请选择要删除的成员')
        return
      }
      
      const count = this.selectedMemberIds.length
      this.$confirm(`确定要移除选中的 ${count} 个成员吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await delGroupMembers({
            group_id: this.groupId,
            user_ids: this.selectedMemberIds
          })
          
          this.$message.success(`成功移除 ${count} 个成员`)
          
          // 清空选择
          this.selectedMemberIds = []
          
          // 重新加载列表
          await this.loadUserList()
          
          // 通知父组件刷新
          this.$emit('members-updated')
        } catch (error) {
          console.error('批量删除成员失败:', error)
          this.$message.error(error.message || '批量删除成员失败')
        }
      }).catch(() => {
        // 取消删除
      })
    },
    
    /**
     * 关闭对话框
     */
    handleClose() {
      this.dialogVisible = false
      this.searchKeyword = ''
      this.memberSearchKeyword = ''
      this.currentPage = 1
      this.currentMemberPage = 1
      this.selectedUserIds = []
      this.selectedMemberIds = []
    }
  }
}
</script>

<style scoped>
/* 对话框标题样式 */
.dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.dialog-title i {
  font-size: 20px;
  color: #409EFF;
}

.view-only-tag {
  margin-left: 10px;
}

/* 主容器 */
.member-selector-container {
  display: flex;
  align-items: stretch;
  gap: 24px;
  padding: 10px 0;
  min-height: 500px;
}

/* 穿梭框面板 */
.transfer-panel {
  flex: 1;
  min-width: 0;
  border: 1px solid #E4E7ED;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.transfer-panel:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.left-panel {
  border-top: 3px solid #409EFF;
}

.right-panel {
  border-top: 3px solid #67C23A;
}

/* 面板头部 */
.panel-header {
  height: 56px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  padding: 0 20px;
  border-bottom: 1px solid #E4E7ED;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-left i {
  font-size: 18px;
  color: #409EFF;
}

.right-panel .header-left i {
  color: #67C23A;
}

.panel-title {
  font-size: 15px;
  color: #303133;
  font-weight: 600;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  background: linear-gradient(135deg, #ecf5ff 0%, #e1f0ff 100%);
  color: #409EFF;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  border: 1px solid #d9ecff;
  min-width: 45px;
  transition: all 0.3s ease;
}

.count-badge.active {
  background: linear-gradient(135deg, #f0f9ff 0%, #e1f3d8 100%);
  color: #67C23A;
  border: 1px solid #c2e7b0;
}

.select-all-checkbox >>> .el-checkbox__inner {
  border-radius: 4px;
}

/* 面板主体 */
.panel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #ffffff;
}

.search-wrapper {
  padding: 16px;
  background-color: #fafbfc;
  border-bottom: 1px solid #eef0f3;
}

.search-wrapper >>> .el-input__inner {
  border-radius: 20px;
  border: 1px solid #dcdfe6;
  transition: all 0.3s ease;
}

.search-wrapper >>> .el-input__inner:focus {
  border-color: #409EFF;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

/* 列表内容 */
.list-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
}

.list-content::-webkit-scrollbar {
  width: 6px;
}

.list-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.list-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
  transition: background 0.3s ease;
}

.list-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.list-content >>> .el-checkbox-group {
  width: 100%;
}

/* 用户卡片 */
.user-card {
  margin-bottom: 8px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.user-card:hover {
  background: #f7f9fc;
  border-color: #409EFF;
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.user-card.selected {
  background: linear-gradient(135deg, #ecf5ff 0%, #e1f0ff 100%);
  border-color: #409EFF;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}

.user-card.readonly {
  cursor: default;
  background: #fafafa;
}

.user-card.readonly:hover {
  transform: none;
  box-shadow: none;
  border-color: #e8eaed;
  background: #fafafa;
}

.user-checkbox {
  width: 100%;
}

.user-card >>> .el-checkbox {
  width: 100%;
  display: flex;
  align-items: center;
}

.user-card >>> .el-checkbox__input {
  margin-top: 0;
}

.user-card >>> .el-checkbox__label {
  flex: 1;
  padding-left: 12px;
  width: 100%;
}

/* 用户信息 */
.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.user-avatar.active {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  box-shadow: 0 2px 8px rgba(56, 239, 125, 0.3);
}

.user-card:hover .user-avatar {
  transform: scale(1.1) rotate(5deg);
}

.user-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dept {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dept i {
  font-size: 12px;
  color: #C0C4CC;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  color: #DCDFE6;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
  color: #909399;
}

/* 分页 */
.pagination-wrapper {
  padding: 12px 16px;
  border-top: 1px solid #eef0f3;
  background-color: #fafbfc;
  display: flex;
  justify-content: center;
}

.pagination-wrapper >>> .el-pagination {
  display: flex;
  justify-content: center;
}

.pagination-wrapper >>> .el-pager li {
  border-radius: 4px;
  transition: all 0.3s ease;
}

/* 中间按钮区域 */
.transfer-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 0 20px;
}

.action-button {
  width: 48px;
  height: 48px;
  padding: 0;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-button:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.action-button:active:not(:disabled) {
  transform: scale(0.95);
}

.remove-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.remove-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.add-btn {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  border: none;
}

.add-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #38ef7d 0%, #11998e 100%);
}

.action-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
}

.view-only-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%);
  color: #757575;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 对话框底部 */
.dialog-footer >>> .el-button {
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.dialog-footer >>> .el-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 对话框自定义类 */
.member-selector-dialog >>> .el-dialog__header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #E4E7ED;
}

.member-selector-dialog >>> .el-dialog__body {
  padding: 24px;
  background-color: #f8f9fa;
}

.member-selector-dialog >>> .el-dialog__footer {
  padding: 16px 24px;
  background-color: #ffffff;
  border-top: 1px solid #E4E7ED;
}

.member-selector-dialog >>> .el-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

/* 响应式设计 */
@media screen and (max-width: 1024px) {
  .member-selector-container {
    gap: 16px;
  }
  
  .transfer-buttons {
    padding: 0 12px;
  }
  
  .action-button {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
}
</style>

